import { useEffect, useCallback } from 'react';
import { useWorkspaceContext } from '@/contexts/WorkspaceContext';
import { getCurrentPageTitle } from '@/hooks/usePageTitle';
import { useUnreadTickets } from '@/hooks/useUnreadTickets';

const BASE_TITLE = 'Luminjo';

/**
 * Builds the document title with page, workspace and base title
 * Format: "Page - Workspace - Luminjo" or "Workspace - Luminjo" or "Luminjo"
 */
function buildBaseTitle(pageTitle: string | null, workspaceName: string | null): string {
  const parts: string[] = [];
  
  if (pageTitle) {
    parts.push(pageTitle);
  }
  
  if (workspaceName) {
    parts.push(workspaceName);
  }
  
  parts.push(BASE_TITLE);
  
  return parts.join(' - ');
}

/**
 * Hook to update the document title with unread ticket count and workspace name
 * Shows format: "(3) Page - WorkspaceName - Luminjo" when there are unread tickets
 * Works in coordination with usePageTitle hook
 * 
 * Uses the shared useUnreadTickets hook to ensure synchronization with the sidebar badge
 */
export function useDocumentTitle() {
  const { activeWorkspace } = useWorkspaceContext();
  const unreadCount = useUnreadTickets();

  const updateTitle = useCallback(() => {
    // Get current page title from the shared state
    const pageTitle = getCurrentPageTitle();
    const workspaceName = activeWorkspace?.name || null;
    const baseTitle = buildBaseTitle(pageTitle, workspaceName);

    // Update document title with count prefix if needed
    if (unreadCount > 0) {
      document.title = `(${unreadCount}) ${baseTitle}`;
    } else {
      document.title = baseTitle;
    }
  }, [activeWorkspace?.name, unreadCount]);

  useEffect(() => {
    updateTitle();

    // Listen for page title changes from usePageTitle
    const handlePageTitleChange = () => {
      updateTitle();
    };
    window.addEventListener('pagetitle-changed', handlePageTitleChange);

    return () => {
      window.removeEventListener('pagetitle-changed', handlePageTitleChange);
    };
  }, [updateTitle]);
}
